import * as React from "react";

import { PkcePreAuthorization } from "../../services/bedrock/dtos/OauthApplication";
import { OauthPkceServices } from "../../services/bedrock/OauthPkceServices";
import { useEffect } from "react";
import { URLS } from "../../utils";
import { Button } from "@/components/ui";
import { Png } from "../../assets";
import _ from "lodash";
import { AppContext } from "@/providers/AppContextProvider";
import { ArrowRightLeft, Eye, Pencil, Shield } from "lucide-react";

const SCOPE_CONFIG: Record<string, { icon: React.ElementType; label: string; description: string }> = {
  read: {
    icon: Eye,
    label: "Read",
    description: "View and retrieve your account data",
  },
  write: {
    icon: Pencil,
    label: "Write",
    description: "Create, update, and delete your data",
  },
};

const PkceAuthorizationClass: React.FC<{}> = () => {
  const [preAuth, setPreAuth] = React.useState<PkcePreAuthorization>();

  const { client_id, redirect_uri, code_challenge, scope } = Object.fromEntries(
    new URLSearchParams(window.location.search).entries()
  );
  const { userContext } = React.useContext(AppContext);

  useEffect(() => {
    if (!client_id || !redirect_uri || !code_challenge) {
      console.error("Missing required OAuth parameters");
      window.location.href = URLS.login;
      return;
    }
    OauthPkceServices.getPkceAuthorization(client_id, redirect_uri, code_challenge, scope)
      .then(data => {
        if (data.status === "redirect") {
          window.location.href = data.redirect_uri;
        } else {
          setPreAuth(data);
        }
      })
      .catch(error => {
        console.error("Error fetching OAuth application:", error);
        window.location.href = URLS.login;
      });
  }, []);

  const authorizeOauthApplication = async () => {
    if (preAuth) {
      try {
        const response = await OauthPkceServices.authorizePkce(client_id, redirect_uri, code_challenge, scope);
        window.location.href = response.redirect_uri;
      } catch (error) {
        console.error("Authorization failed:", error);
      }
    }
  };

  const declineOauthApplication = () => {
    const url = new URL(redirect_uri);
    url.searchParams.append("error", "access_denied");
    url.searchParams.append("error_description", "The user denied the authorization request");
    window.location.href = url.toString();
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-start bg-gray-50 p-4">
      <img src={Png.Logo} alt="SocialCoach" className="mb-6 mt-8 h-16" />

      {preAuth && (
        <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
          <h1 className="mb-6 text-center !font-semibold !text-2xl">Authorize App</h1>

          <div className="mb-6 flex items-center justify-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-primary">
              {userContext?.profile.photoUrl ? (
                <img
                  src={userContext.profile.photoUrl}
                  alt={userContext.profile.fullName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="!text-xl font-semibold text-gray-600">
                  {userContext?.profile.fullName?.charAt(0) || "?"}
                </span>
              )}
            </div>
            <ArrowRightLeft className="h-8 w-8 text-gray-400" />
            {preAuth.icon_url ? (
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg">
                <img src={preAuth.icon_url} alt={preAuth.client_name} className="h-full w-full object-cover" />
              </div>
            ) : (
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg bg-secondary">
                <span className="!text-2xl font-bold text-white">{preAuth.client_name?.charAt(0) || "A"}</span>
              </div>
            )}
          </div>

          <div className="mb-6 text-center">
            <p className="text-gray-700">
              Hi <span className="font-semibold">{userContext?.profile.fullName || userContext?.account.email}</span>,
            </p>
            <p className="mt-1 text-gray-700">
              <span className="font-semibold">{preAuth.client_name}</span> is requesting access to your{" "}
              <span className="font-semibold">SocialCoach</span> account.
            </p>
          </div>

          <div className="mb-6 space-y-2">
            {preAuth.scope.split(" ").map((scopeKey, index) => {
              const config = SCOPE_CONFIG[scopeKey.toLowerCase()] ?? {
                icon: Shield,
                label: _.capitalize(scopeKey),
                description: "Perform actions on your behalf",
              };
              const Icon = config.icon;
              return (
                <div
                  key={index}
                  className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                    <Icon className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-700">{config.label}</span>
                    <span className="text-xs text-gray-500">{config.description}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col gap-3">
            <Button variant="default" className="w-full" onClick={authorizeOauthApplication}>
              Authorize
            </Button>
            <Button variant="outline" className="w-full" onClick={declineOauthApplication}>
              Decline
            </Button>
          </div>
        </div>
      )}

      {!preAuth && (
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
          <p className="text-sm text-gray-500">Loading authorization details...</p>
        </div>
      )}
    </div>
  );
};

export const PkceAuthorization = PkceAuthorizationClass;
